<template>
    <div
        v-if="open"
        class="Modal"
    >
        <div
            class="Modal__background"
            @click="closeModal"
        ></div>
        <div class="Modal__modal">
            <slot name="modal">
                <div class="Modal__header">
                    <span :class="headerClass"></span>
                    <div
                        v-if="header !== ''"
                        class="Modal__header-bar"
                    >
                        <!-- @slot Can be used in place of the `header` prop -->
                        <slot name="modalHeader">
                            <h5 class="Modal__header-text">
                                {{ header }}
                            </h5>
                        </slot>

                        <span
                            class="Modal__close-icon"
                            @click="closeModal"
                        >
                            <StreamlineIcon
                                :icon="FormValidationClose"
                                fill="#1B3449"
                                :size="14"
                            />
                        </span>
                    </div>
                </div>
                <div class="Modal__body">
                    <!-- @slot The body of the modal -->
                    <slot name="modalBody"></slot>
                </div>
                <div
                    v-if="showActionButtons"
                    :class="buttonContainerClass"
                >
                    <template v-if="!isLoading">
                        <!-- @slot Replace the entire action buttons section -->
                        <slot name="modalButtons">
                            <RPButton
                                class="Modal__action-button"
                                :class="actionButtonClass"
                                :disabled="disableModalSubmit || isSaving"
                                :status="status"
                                :label="buttonText"
                                @click="submitModal"
                            />
                            <RPButton
                                v-if="showCancel"
                                class="Modal__action-button Modal__action-button--half"
                                :label="cancelButtonText"
                                type="tertiary"
                                @click="closeModal"
                            />
                        </slot>
                    </template>
                </div>
            </slot>
        </div>
    </div>
</template>
<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {Close as FormValidationClose} from "@reviewpush/streamline-regular/lib/interface-essential/FormValidation";
import RPButton from 'components/Basic/Buttons/RPButton.vue';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';

export default {
    name: 'RPModal',
    components: {
        StreamlineIcon,
        RPButton
    },
    props: {
        open: {
            type: Boolean,
            default: true
        },
        /**
         * Text to display in the modal header
         */
        header: {
            type: String,
            default: ''
        },
        /**
         * Set to `true` to disable the submit button
         */
        disableModalSubmit: {
            type: Boolean,
            default: false,
        },
        /**
         * Hide or show the entire action buttons section
         */
        showActionButtons: {
            type: Boolean,
            default: true,
        },
        /**
         * Hide or show the cancel button
         */
        showCancel: {
            type: Boolean,
            default: true,
        },
        /**
         * Text for the submit button
         */
        submitButtonText: {
            type: String,
            default: ''
        },
        /**
         * Text for the cancel button
         */
        cancelButtonText: {
            type: String,
            default: 'Cancel'
        },
        status: {
            type: String,
            default: stat.STANDBY
        }
    },
    data(){
        return {
            FormValidationClose,
        };
    },
    computed: {
        isSaving(){
            return this.status === stat.SAVING;
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        buttonText(){
            return this.status === stat.STANDBY
                ? this.submitButtonText
                : '';
        },
        buttonContainerClass(){
            return statusBuilder('Modal__button-container', this.status);
        },
        headerClass(){
            return statusBuilder('Modal__header-status', this.status);
        },
        actionButtonClass(){
            return statusBuilder('Modal__action-button', '', false, {half: this.showCancel, full: !this.showCancel});
        },
    },
    methods: {
        closeModal(){
            /**
             * Close the modal
             */
            this.$emit('close-modal');
        },
        submitModal(){
            /**
             * Triggered when the user clicks the modal's `submit` button
             */
            this.$emit('submit-modal');
        }
    }
};
</script>
<style lang="scss">
    .Modal {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 100;
        @media(min-width: $max-md-width){
            display: flex;
            justify-content: center;
            align-items: center;
        }

        &__background {
            position: absolute;
            background: rgba(2, 18, 30, 0.5);
            height: 100%;
            width: 100%;
        }

        &__modal {
            background: $white;
            border-radius: 4px;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
            z-index: 101;

            @media(min-width: $max-md-width){
                min-width: 10rem;
            }
            @media(max-width: $max-md-width){
                width:100%;
            }
        }
        &__header {
            display: flex;
            flex-direction: column;
        }
        &__header-bar {
            position: relative;
            padding: 1rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid $grey-75;
        }
        &__header-status {
            display: block;
            width: 100%;
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
            height: 0.25rem;
            @include loading(0.25rem, 100%);
            background: $primary;
            &--success { background-image: $gradient-positive; }
            &--warning { background-image: $gradient-yellow; }
            &--error { background-image: $gradient-negative; }
            &--saving { background-image: $gradient-purple; }
            &--custom { background-image: none; }
        }
        &__header-text {
            color: $text-dark;
        }
        &__close-icon {
            display: flex;
            align-items: center;
            cursor: pointer;
            margin: auto 0 auto 0.5rem;
        }
        &__button-container {
            @include loading(2rem, 100%);
            justify-content: center;
            border-top: 1px solid #EAEDF2;
            padding: 1rem;
            display: flex;
        }
        &__action-button{
            flex-grow: 1;
            max-width: 15rem;
            &--half {
                &:not(:last-child){
                    margin-right: 1rem;
                }
            }
        }
    }
</style>

<docs>
```[import](./RPModalExample.vue)
```
</docs>
